import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment-timezone";

const moment = getMomentTz();

export const TEST_SHACK_MODE_VALUES = ["period", "shift"] as const;

export type TestShackMode = (typeof TEST_SHACK_MODE_VALUES)[number];

export const TEST_SHACK_DATE_PRESET_VALUES = [
	"last7days",
	"last30days",
	"week",
	"month",
	"year",
] as const;

export type TestShackDatePreset =
	(typeof TEST_SHACK_DATE_PRESET_VALUES)[number];

interface TestShackDateRange {
	start: Moment;
	end: Moment;
}

const presetRangeFactories: Record<
	TestShackDatePreset,
	(today: Moment) => TestShackDateRange
> = {
	last7days: (today) => ({
		start: today.clone().subtract(6, "days"),
		end: today.clone().endOf("day"),
	}),
	last30days: (today) => ({
		start: today.clone().subtract(29, "days"),
		end: today.clone().endOf("day"),
	}),
	week: (today) => {
		const start = today.clone().day(0);
		return { start, end: start.clone().add(6, "days").endOf("day") };
	},
	month: (today) => ({
		start: today.clone().startOf("month"),
		end: today.clone().endOf("month"),
	}),
	year: (today) => ({
		start: today.clone().startOf("year"),
		end: today.clone().endOf("year"),
	}),
};

export const getTestShackPresetRange = (
	preset: TestShackDatePreset,
	now: Moment = moment(),
): TestShackDateRange =>
	presetRangeFactories[preset](now.clone().startOf("day"));

const hasTestShackPresetShape = (
	preset: TestShackDatePreset,
	start: Moment,
	end: Moment,
): boolean => {
	const first = start.clone().startOf("day");
	const last = end.clone().startOf("day");
	const days = last.diff(first, "days") + 1;

	if (preset === "year" || preset === "month") {
		return (
			first.isSame(first.clone().startOf(preset), "day") &&
			last.isSame(first.clone().endOf(preset), "day")
		);
	}

	if (preset === "week") return first.day() === 0 && days === 7;

	const expectedDays = preset === "last7days" ? 7 : 30;
	return days === expectedDays && last.isSame(moment(), "day");
};

export const shiftTestShackDateRange = (
	start: Moment,
	end: Moment,
	direction: 1 | -1,
	preset: TestShackDatePreset | null,
): TestShackDateRange => {
	const first = start.clone().startOf("day");
	const last = end.clone().startOf("day");

	if (preset === "year" || preset === "month") {
		const moved = first.clone().add(direction, preset);
		return {
			start: moved.clone().startOf(preset),
			end: moved.clone().endOf(preset),
		};
	}

	const step = (last.diff(first, "days") + 1) * direction;
	return {
		start: first.clone().add(step, "days"),
		end: last.clone().add(step, "days").endOf("day"),
	};
};

export const TEST_SHACK_DATE_FORMAT = "YYYY-MM-DD";

export const DEFAULT_TEST_SHACK_DATE_PRESET: TestShackDatePreset = "last7days";

interface TestShackResolvedRange extends TestShackDateRange {
	datePreset: TestShackDatePreset | null;
	hasInvalidDate: boolean;
}

const parseUrlDate = (
	value: string | null,
	edge: "start" | "end",
): Moment | null => {
	if (value === null) return null;

	const parsed = moment(value, TEST_SHACK_DATE_FORMAT, true);
	if (!parsed.isValid()) return null;

	return edge === "start" ? parsed.startOf("day") : parsed.endOf("day");
};

export const resolveTestShackUrlRange = (
	from: string | null,
	to: string | null,
	preset: TestShackDatePreset,
): TestShackResolvedRange => {
	const defaultRange = getTestShackPresetRange(DEFAULT_TEST_SHACK_DATE_PRESET);
	const parsedStart = parseUrlDate(from, "start");
	const parsedEnd = parseUrlDate(to, "end");
	const candidateStart = parsedStart ?? defaultRange.start;
	const candidateEnd = parsedEnd ?? defaultRange.end;
	const hasInvalidDate =
		(from !== null && parsedStart === null) ||
		(to !== null && parsedEnd === null) ||
		candidateStart.isAfter(candidateEnd, "day");
	const start = hasInvalidDate ? defaultRange.start : candidateStart;
	const end = hasInvalidDate ? defaultRange.end : candidateEnd;

	return {
		start,
		end,
		datePreset: hasTestShackPresetShape(preset, start, end) ? preset : null,
		hasInvalidDate,
	};
};
